import React, { useRef, useEffect, useState } from 'react';
import { useForm } from '../../contexts/FormContext';
import { useLocation } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import SceneSizeUp from './sections/SceneSizeUp';
import PrimaryAssessment from './sections/PrimaryAssessment';
import SecondaryAssessment from './sections/SecondaryAssessment';
import Pathophysiology from './sections/Pathophysiology';
import FieldDiagnosis from './sections/FieldDiagnosis';
import Documentation from './sections/Documentation';
import FormControls from './FormControls';
import AutoSave from './AutoSave';
import { getStudentByUID } from '../../utils/studentMapUtils';

interface PCRFormPageProps {
  isReadOnly?: boolean;
}

const PCRFormPage: React.FC<PCRFormPageProps> = ({ isReadOnly = false }) => {
  const { loading, hasUnsavedChanges, formData } = useForm();
  const location = useLocation();
  const formRef = useRef<HTMLDivElement>(null);
  const [studentInfo, setStudentInfo] = useState<{ name: string; email: string } | null>(null);
  const [sectionOnlyMode, setSectionOnlyMode] = useState(false);
  const [sectionToShow, setSectionToShow] = useState<string | null>(null);

  // Check for section-only mode from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const section = params.get('section');
    
    if (mode === 'section-only' && section) {
      setSectionOnlyMode(true);
      setSectionToShow(section);
    } else {
      setSectionOnlyMode(false);
      setSectionToShow(null);
    }
  }, [location.search]);
  
  // Add this useEffect to get student info when form data changes
  useEffect(() => {
    // Existing student info code
    if (formData && formData.userId) {
      // Try to get student info from the local mapping
      const student = getStudentByUID(formData.userId);
      if (student) {
        setStudentInfo({
          name: student.name,
          email: student.email
        });
      } else {
        // Use stored values if available
        if (formData.studentName || formData.studentEmail) {
          setStudentInfo({
            name: formData.studentName || '',
            email: formData.studentEmail || ''
          });
        } else {
          setStudentInfo(null);
        }
      }
    }
    console.log("Form data structure:", JSON.stringify(formData, null, 2));
  }, [formData]);

  // Add this useEffect for auto-resizing all textareas
  useEffect(() => {
    // Existing textarea resize code
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        // Save current scroll position
        const scrollPos = window.pageYOffset;
        
        // Reset height to auto and set to scrollHeight
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
        
        // Restore scroll position
        window.scrollTo(0, scrollPos);
      });
    };
    
    // Initial resize after content has loaded
    if (!loading) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(resizeAllTextareas, 100);
    }
    
    // Add input event listener to all textareas
    const handleInput = (e: Event): void => {
      if (e.target && (e.target as HTMLElement).tagName.toLowerCase() === 'textarea') {
        const textarea = e.target as HTMLTextAreaElement;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    };
    
    document.addEventListener('input', handleInput);
    
    // Cleanup
    return () => {
      document.removeEventListener('input', handleInput);
    };
  }, [loading, formData]); // Re-run when loading or form data changes

  // Function to determine which section to render in section-only mode
  const renderIsolatedSection = () => {
    if (!sectionToShow) return null;
    
    // Simple header to provide context
    const header = (
      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        {getSectionTitle(sectionToShow)}
        {studentInfo?.name && <span className="block text-sm font-normal text-gray-600 mt-1">{studentInfo.name}</span>}
      </h2>
    );
    
    switch(sectionToShow) {
      // Primary Assessment Sections
      case 'chief-complaint':
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="isolated-chief-complaint" className="block text-sm font-medium text-gray-700">
                  Chief Complaint
                </label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {formData.primaryAssessment.chiefComplaint || 
                  <span className="text-gray-500 italic">No chief complaint recorded</span>
                }
              </div>
            </div>
          </>
        );
        
      // Other primary assessment sections remain unchanged
        
      // OPQRST sections - use hpi instead of secondaryAssessment
      case 'onset':
      case 'provoke':
      case 'quality':
      case 'radiate-region':
      case 'severity':
      case 'time':
        // Convert 'radiate-region' to 'radiateRegion' for property access
        const opqrstField = sectionToShow === 'radiate-region' ? 'radiateRegion' : 
                          sectionToShow === 'provoke' ? 'provocation' : sectionToShow;
        const opqrstLabel = getSectionTitle(sectionToShow);
        
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  {opqrstLabel}
                </label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {/* Use hpi instead of secondaryAssessment.OPQRST */}
                {formData.hpi[opqrstField as keyof typeof formData.hpi] || 
                  <span className="text-gray-500 italic">No {opqrstLabel.toLowerCase()} details recorded</span>
                }
              </div>
            </div>
          </>
        );
        
      // SAMPLE sections - use pmh instead of secondaryAssessment
      case 'allergies':
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Allergies</label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {/* Use pmh instead of secondaryAssessment.SAMPLE */}
                {formData.pmh.allergies || 
                  <span className="text-gray-500 italic">No allergies recorded</span>
                }
              </div>
            </div>
          </>
        );
        
      case 'medications':
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Medications</label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {/* Use pmh instead of secondaryAssessment.SAMPLE */}
                {formData.pmh.medications || 
                  <span className="text-gray-500 italic">No medications recorded</span>
                }
              </div>
            </div>
          </>
        );
        
      case 'pertinent-history':
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Pertinent Medical History</label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {/* Use pmh.pertinentPastHistory instead of secondaryAssessment.SAMPLE.pertinentHistory */}
                {formData.pmh.pertinentPastHistory || 
                  <span className="text-gray-500 italic">No pertinent history recorded</span>
                }
              </div>
            </div>
          </>
        );
        
      // Update treatment-interventions to use the correct field name
      case 'treatment-interventions':
        return (
          <>
            {header}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Treatment & Interventions
                </label>
              </div>
              <div className="mt-1 block w-full rounded-md border-2 border-gray-200 shadow-inner bg-gray-50 p-3 min-h-[100px]">
                {/* Use treatmentAndInterventions (with 'And') instead of treatmentInterventions */}
                {formData.fieldDiagnosis.treatmentAndInterventions || 
                  <span className="text-gray-500 italic">No treatment/interventions recorded</span>
                }
              </div>
            </div>
          </>
        );
      
      // Other sections remain the same
      
      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <p className="text-gray-500">Section "{sectionToShow}" not found or not implemented for isolated view.</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <main className="min-h-screen bg-gray-100 p-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          </div>
        </main>
      </div>
    );
  }
  
  // Section-only mode rendering
  if (sectionOnlyMode && sectionToShow) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* No navbar in section-only mode for cleaner embedded view */}
        <main className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-3xl mx-auto">
            {renderIsolatedSection()}
          </div>
        </main>
      </div>
    );
  }
  
  // Regular full-form rendering (your existing code)
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <main ref={formRef} className="min-h-screen bg-gray-100 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">PCR 4</h1>
              {studentInfo && studentInfo.name && (
                <p className="text-md text-gray-600 mt-1">
                  Student: <span className="font-semibold">{studentInfo.name}</span>
                </p>
              )}
              {studentInfo && studentInfo.email && (
                <p className="text-sm text-gray-500">
                  {studentInfo.email}
                </p>
              )}
              {/* Display user ID if no student info is available */}
              {(!studentInfo || (!studentInfo.name && !studentInfo.email)) && (
                <p className="text-sm text-gray-500 mt-1">
                  User ID: {formData.userId}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-medium"> </p>
              <p className="text-sm text-gray-600"></p>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 italic mb-8">
            After reading the Dispatch Scenario below, you must simulate a medically accurate patient care report. You will be given the general outline of your patient encounter, including important details that will help you build your differential diagnosis. The information given to you in the scenario should be included in your report, e.g., things the patient or witnesses said, the initial vital signs given to you, medications, etc. You will use this partial scenario as a building block in which you must complete. All portions of the assignment that are not given to you should be filled in from the perspective of your run, with medically accurate findings and realistic outcomes. All of your findings should ultimately paint a clear picture of your Field Daignosis. You should refer to your book, Paramedic Care, to ensure you understand the goals of each section.
          </p>
          
          <div id="form-content" className="space-y-8">
            <div className="bg-gradient-to-br from-gray-200 to-white rounded shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.2)] mb-6">
              <h4 className="text-lg font-semibold text-gray-700 p-4 border-b border-gray-300">Dispatch Scenario</h4>
              <p className="px-4 pt-2 text-sm text-gray-600 italic"></p>
              <div className="p-4 whitespace-pre-line text-gray-700">
                {formData.dispatchScenario}
              </div>
            </div>
            
            <SceneSizeUp />
            <PrimaryAssessment />
            <SecondaryAssessment />
            <Pathophysiology />
            <FieldDiagnosis />
            <Documentation />
          </div>
        </div>
      </main>
      
      {/* Add the AutoSave component with a 1-minute interval */}
      {!isReadOnly && <AutoSave interval={60000} idleThreshold={180000} />}
      
      {/* Use the FormControls component here instead of duplicating code */}
      <FormControls 
        studentInfo={studentInfo} 
        formData={formData} 
        formId={formData.id || ''} 
        isReadOnly={isReadOnly}
      />
      
      {/* Display unsaved changes indicator (hidden in read-only mode) */}
      {hasUnsavedChanges && !isReadOnly && (
        <div className="fixed top-16 right-4 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md shadow-md text-sm z-50">
          Unsaved changes
        </div>
      )}
      
      {/* Display read-only indicator */}
      {isReadOnly && (
        <div className="fixed top-16 right-4 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md shadow-md text-sm z-50">
          Read-Only Mode
        </div>
      )}
    </div>
  );
};

// Helper function to get human-readable section titles
function getSectionTitle(section: string): string {
  const titles: Record<string, string> = {
    'chief-complaint': 'Chief Complaint',
    'mental-status': 'Level of Consciousness & Mental Status',
    'airway-assessment': 'Airway Assessment',
    'breathing-assessment': 'Breathing Assessment',
    'circulation-assessment': 'Circulation Assessment',
    'gcs': 'Glasgow Coma Scale (GCS)',
    'patient-priority': 'Patient Priority Decision',
    'onset': 'Onset (OPQRST)',
    'provoke': 'Provocation (OPQRST)',
    'quality': 'Quality (OPQRST)',
    'radiate-region': 'Radiation/Region (OPQRST)',
    'severity': 'Severity (OPQRST)',
    'time': 'Time (OPQRST)',
    'allergies': 'Allergies',
    'medications': 'Medications',
    'pertinent-history': 'Pertinent Medical History',
    'pathophysiology': 'Pathophysiology Pathways',
    'field-diagnosis': 'Field Diagnosis Steps',
    'treatment-interventions': 'Treatment & Interventions',
    'narrative': 'Chart Narrative',
    'radio-report': 'Radio Report'
  };
  
  return titles[section] || `Section: ${section}`;
}

export default PCRFormPage;